// Lightweight, dependency-free PDF generator for service invoices.
// Produces a valid PDF 1.4 file using only built-in fonts (Helvetica).

function escapePdf(s) {
  return String(s).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

export function formatRand(n) {
  const v = Number(n || 0);
  return `R ${v.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatKm(n) {
  const v = Number(n || 0);
  return `${v.toLocaleString("en-ZA")} km`;
}

function buildContentStream(record, vehicle, customer) {
  const lines = [];
  const push = (cmd) => lines.push(cmd);

  // Title
  push("BT /F2 22 Tf 50 770 Td (WrenchOnWheels) Tj ET");
  push("BT /F1 10 Tf 50 752 Td (On-demand mobile mechanics  -  South Africa) Tj ET");

  // Invoice header
  push("BT /F2 16 Tf 50 710 Td (SERVICE INVOICE) Tj ET");
  push(`BT /F1 10 Tf 400 710 Td (Invoice #: ${escapePdf(record.id || "INV-0001")}) Tj ET`);
  push(
    `BT /F1 10 Tf 400 696 Td (Date: ${escapePdf(new Date(record.date).toLocaleDateString("en-ZA"))}) Tj ET`
  );

  // Bill to
  push("BT /F2 11 Tf 50 670 Td (Billed To) Tj ET");
  push(`BT /F1 10 Tf 50 656 Td (${escapePdf(customer?.name || "Customer")}) Tj ET`);
  push(`BT /F1 10 Tf 50 642 Td (Username: ${escapePdf(customer?.username || "-")}) Tj ET`);

  // Vehicle
  push("BT /F2 11 Tf 320 670 Td (Vehicle) Tj ET");
  if (vehicle) {
    push(
      `BT /F1 10 Tf 320 656 Td (${escapePdf(vehicle.year || "")} ${escapePdf(vehicle.make || "")} ${escapePdf(vehicle.model || "")}) Tj ET`
    );
    push(`BT /F1 10 Tf 320 642 Td (Reg: ${escapePdf(vehicle.reg || "-")}) Tj ET`);
    push(`BT /F1 10 Tf 320 628 Td (VIN: ${escapePdf(vehicle.vin || "-")}) Tj ET`);
  } else {
    push(`BT /F1 10 Tf 320 656 Td (Odometer: ${escapePdf(formatKm(record.km))}) Tj ET`);
  }

  // Service block
  push("BT /F2 11 Tf 50 600 Td (Service Performed) Tj ET");
  push(`BT /F1 10 Tf 50 586 Td (${escapePdf(record.service)}) Tj ET`);
  push(`BT /F1 10 Tf 50 572 Td (Mechanic: ${escapePdf(record.mechanic)}) Tj ET`);
  push(`BT /F1 10 Tf 50 558 Td (Odometer: ${escapePdf(formatKm(record.km))}) Tj ET`);

  // Parts table
  push("BT /F2 11 Tf 50 528 Td (Parts & Materials) Tj ET");
  let y = 514;
  (record.parts || []).forEach((p) => {
    push(`BT /F1 10 Tf 60 ${y} Td (- ${escapePdf(p)}) Tj ET`);
    y -= 14;
  });

  // Notes
  if (record.notes) {
    y -= 10;
    push(`BT /F2 11 Tf 50 ${y} Td (Mechanic Notes) Tj ET`);
    y -= 14;
    const wrap = String(record.notes).match(/.{1,90}(\s|$)/g) || [record.notes];
    wrap.forEach((line) => {
      push(`BT /F1 10 Tf 50 ${y} Td (${escapePdf(line.trim())}) Tj ET`);
      y -= 14;
    });
  }

  // Total
  push("BT /F2 12 Tf 50 110 Td (TOTAL DUE) Tj ET");
  push(`BT /F2 14 Tf 460 110 Td (${escapePdf(formatRand(record.amount))}) Tj ET`);
  push("BT /F1 9 Tf 50 80 Td (Payment received with thanks. All amounts in South African Rand.) Tj ET");
  push("BT /F1 9 Tf 50 66 Td (WrenchOnWheels  -  support@wrenchonwheels.co.za) Tj ET");

  return lines.join("\n");
}

export function generateInvoicePdf(record, vehicle, customer) {
  const stream = buildContentStream(record, vehicle, customer);
  const streamBytes = new TextEncoder().encode(stream);

  const objects = [];
  objects.push("<< /Type /Catalog /Pages 2 0 R >>");
  objects.push("<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
  objects.push(
    "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents 4 0 R /Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> >>"
  );
  objects.push(`<< /Length ${streamBytes.length} >>\nstream\n${stream}\nendstream`);
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>");

  let pdf = "%PDF-1.4\n";
  const offsets = [];
  objects.forEach((body, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((o) => {
    pdf += `${String(o).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

  const blob = new Blob([pdf], { type: "application/pdf" });
  return blob;
}

export function downloadInvoice(record, vehicle, customer) {
  const blob = generateInvoicePdf(record, vehicle, customer);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-${record.id || Date.now()}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
